import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import { MARTINGALE_BAND_PRODUCT_ID } from '../../lib/product';
import { cartActions as storeCartActions } from '../../reducers/storeCart';
import * as types from '../../types';
import { series2CollarId, series3CollarId, series3PlusCollarId } from '../../types';
import EndlinksDetails from '../Endlinks';
import NotFound from '../NotFound';
import BandDetails from './BandDetails';
import ChargingBaseDetails from './ChargingBaseDetails';
import MartingaleBandDetails from './MartingaleBandDetails';
import NanoDetails from './NanoDetails';
import Series2CollarDetails from './Series2CollarDetails';
import Series3CollarDetails from './Series3CollarDetails';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../lib/growthbook';
import { useLocale } from '../../contexts/LocaleContext';
import { getLocalizedPricing } from '../../lib/util';
import { isInAppShop } from '../../lib/inAppShop';

const SERIES_3_PLUS_MODIFIER = 'b';

export default function ProductDetails() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: productId } = useParams<{ id: string }>();
  const products = useSelector((state: types.AppState) => state.config.products);
  const { currency } = useLocale();

  const seriesThreePlusExperience = useIsGrowthBookEnabled()
    ? getFiGrowthBook().getFeatureValue<boolean>('series-three-plus-experience', false)
    : false;

  const effectiveProductId = useMemo(() => {
    if (seriesThreePlusExperience && productId === series3CollarId) {
      return `${productId}${SERIES_3_PLUS_MODIFIER}`;
    }

    return productId;
  }, [seriesThreePlusExperience, productId]);

  const product = useMemo(() => {
    return products.find((p) => p.id === effectiveProductId);
  }, [effectiveProductId, products]);

  if (!product) {
    return <NotFound />;
  }

  if (product.category === types.ProductCategory.CHARGING_BASE) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return (
      <ChargingBaseDetails
        productId={product.id}
        productName={product.name}
        priceInCents={priceInCents}
        sku={product.sku}
      />
    );
  }

  if (product.category === types.ProductCategory.MICROCHIP) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return <NanoDetails productName={product.name} priceInCents={priceInCents} sku={product.sku} />;
  }

  if (product.category === types.ProductCategory.ENDLINK) {
    return <EndlinksDetails />;
  }

  if (
    product.category === types.ProductCategory.COLLAR_KIT &&
    (product.id === series3CollarId || product.id === series3PlusCollarId)
  ) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return (
      <>
        <Series3CollarDetails
          addBundleCartItem={(variantSku, subscriptionSku, addonSkus) => {
            dispatch(
              storeCartActions.addBundleCartItem({
                sku: variantSku,
                subscriptionSku: subscriptionSku,
                addonSkus: addonSkus,
                currency,
              }),
            );

            if (isInAppShop()) {
              dispatch(storeCartActions.setShowInAppCheckout(true));
            } else {
              history.push(AppPaths.Bag);
            }
          }}
          priceInCents={priceInCents}
          productName={product.name}
          variants={product.variants}
          collarId={product.id}
        />
      </>
    );
  }

  if (product.category === types.ProductCategory.COLLAR_KIT && product.id === series2CollarId) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return <Series2CollarDetails priceInCents={priceInCents} productName={product.name} variants={product.variants} />;
  }

  if (product.category === types.ProductCategory.BAND && product.id === MARTINGALE_BAND_PRODUCT_ID) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return <MartingaleBandDetails priceInCents={priceInCents} productName={product.name} variants={product.variants} />;
  }

  if (product.category === types.ProductCategory.BAND) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return (
      <BandDetails
        bandSeries={product.bandSeries}
        priceInCents={priceInCents}
        productId={product.id}
        productName={product.name}
        variants={product.variants}
      />
    );
  }

  return <NotFound />;
}
